/**
 * @file 文章圖片服務 API
 * @description 提供後台文章圖片管理的 API 方法，包含圖片列表、上傳、更名、刪除功能
 */
import { post, del, postFormData, patchFormData } from './sub_services/base';
import apiPath from './api.path';
import config from 'config/config';
import ExceptionHandleService from 'utils/exceptionHandler';

const _EHS = new ExceptionHandleService({
  _NAME: 'services/postImages.ts',
  _NOTICE: 'postImages api'
});

// ============ Request 介面 ============

/** 取得文章圖片列表請求參數 */
export interface GetPostImagesReq {
  postId: string;
  /** 偏移量，跳過幾筆資料（預設 0） */
  offset?: number;
  /** 每頁筆數（預設 20） */
  limit?: number;
}

/** 上傳圖片請求參數 (不含 file，file 透過 FormData 傳送) */
export interface UploadPostImageReq {
  postId: string;
  imageName: string;
  /** 僅壓縮品質模式（保留原始尺寸，圖片可能超過 1MB） */
  qualityOnly?: boolean;
}

/** 更新圖片請求參數（名稱或圖片檔案） */
export interface UpdatePostImageReq {
  postId: string;
  imageId: string;
  /** 新的圖片名稱（選填） */
  imageName?: string;
  /** 新的圖片檔案（選填） */
  file?: File | Blob;
  /** 僅壓縮品質模式（選填，預設 false） */
  qualityOnly?: boolean;
}

/** 刪除圖片請求參數 */
export interface DeletePostImageReq {
  postId: string;
  imageId: string;
}

/** 批次上傳圖片請求參數 (不含 files，files 透過 FormData 傳送) */
export interface BatchUploadPostImagesReq {
  postId: string;
  /** 僅壓縮品質模式（保留原始尺寸），固定為 false */
  qualityOnly?: boolean;
}

// ============ Response 介面 ============

/** 圖片項目 */
export interface PostImageItem {
  imageId: string;
  imageName: string;
  gcsUrl: string;
  createdAt: string;
}

/**
 * 取得文章圖片列表回應
 * @description apiResponseHandler 會直接回傳 result 部分
 */
export interface GetPostImagesResp {
  images: PostImageItem[];
  /** 圖片總數量 */
  total: number;
  /** 是否還有更多圖片 */
  hasMore: boolean;
}

/**
 * 上傳圖片回應
 * @description apiResponseHandler 會直接回傳 result 部分
 */
export interface UploadPostImageResp {
  imageId: string;
  imageUrl: string;
}

/**
 * 通用回應 (刪除)
 * @description apiResponseHandler 會直接回傳 result 部分
 */
export type CommonResp = null;

/**
 * 更新圖片回應
 * @description apiResponseHandler 會直接回傳 result 部分
 */
export interface UpdatePostImageResp {
  imageId: string;
  imageName: string;
  gcsUrl: string;
  compressedSize: number;
}

/** 批次上傳單一項目結果 */
export interface BatchUploadItemResult {
  /** 原始檔名 */
  fileName: string;
  /** 是否成功 */
  success: boolean;
  /** 圖片 ID（成功時） */
  imageId?: string;
  /** GCS 公開 URL（成功時） */
  gcsUrl?: string;
  /** 壓縮後大小 bytes（成功時） */
  compressedSize?: number;
  /** 錯誤訊息（失敗時） */
  error?: string;
}

/**
 * 批次上傳圖片回應
 * @description apiResponseHandler 會直接回傳 result 部分
 */
export interface BatchUploadPostImagesResp {
  /** 成功數量 */
  successCount: number;
  /** 失敗數量 */
  failureCount: number;
  /** 各檔案上傳結果 */
  items: BatchUploadItemResult[];
}

// ============ API 方法 ============

/**
 * [POST] 取得文章圖片列表（分頁）
 * @param postId - 文章 ID
 * @param offset - 偏移量（預設 0）
 * @param limit - 每頁筆數（預設 20）
 * @returns Promise<GetPostImagesResp> - 圖片列表回應（含分頁資訊）
 */
const getPostImages = async (
  postId: string,
  offset: number = 0,
  limit: number = 20
): Promise<GetPostImagesResp> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.postImages.getPostImages}`;
    const result = await post<GetPostImagesResp, GetPostImagesReq>(url, {
      postId,
      offset,
      limit
    });
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'getPostImages', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/**
 * [POST] 上傳文章圖片
 * @param postId - 文章 ID
 * @param file - 圖片檔案
 * @param imageName - 圖片名稱 (選填，預設使用檔案名稱)
 * @param qualityOnly - 僅壓縮品質模式 (選填，預設 false)
 * @returns Promise<UploadPostImageResp> - 上傳結果
 */
const uploadPostImage = async (
  postId: string,
  file: File | Blob,
  imageName?: string,
  qualityOnly?: boolean
): Promise<UploadPostImageResp> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.postImages.uploadPostImage}`;
    const formData = new FormData();
    formData.append('postId', postId);

    // 如果是 Blob（非 File），轉換為 File 以確保正確的檔案名稱和 MIME type
    // 這對於 HEIC 轉換後的圖片特別重要，避免後端收到 mimetype: 'application/octet-stream'
    const fileToUpload =
      file instanceof Blob && !(file instanceof File)
        ? new File([file], 'converted-image.jpg', { type: 'image/jpeg' })
        : file;

    formData.append('file', fileToUpload);

    // 取得檔案名稱用於 imageName
    const fileName = fileToUpload instanceof File ? fileToUpload.name : 'image.jpg';
    formData.append('imageName', imageName || fileName);

    // 只在 qualityOnly 為 true 時傳送，因為 FormData 只能傳字串
    // 後端預設為 false，所以不傳時自動使用預設值
    if (qualityOnly === true) {
      formData.append('qualityOnly', 'true');
    }

    const result = await postFormData<UploadPostImageResp>(url, formData);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'uploadPostImage', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/**
 * [PATCH] 更新圖片（名稱或圖片檔案）
 * @param postId - 文章 ID
 * @param imageId - 圖片 ID
 * @param options - 更新選項（imageName、file、qualityOnly 至少需提供一項）
 * @returns Promise<UpdatePostImageResp> - 更新結果
 */
const updatePostImage = async (
  postId: string,
  imageId: string,
  options: {
    imageName?: string;
    file?: File | Blob;
    qualityOnly?: boolean;
  }
): Promise<UpdatePostImageResp> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.postImages.updatePostImage}`;
    const formData = new FormData();
    formData.append('postId', postId);
    formData.append('imageId', imageId);

    // 只在有值時加入 FormData
    if (options.imageName !== undefined) {
      formData.append('imageName', options.imageName);
    }

    if (options.file) {
      // 如果是 Blob（非 File），轉換為 File 以確保正確的檔案名稱和 MIME type
      // 這對於裁切後的圖片特別重要，避免後端收到 mimetype: 'application/octet-stream'
      const fileToUpload =
        options.file instanceof Blob && !(options.file instanceof File)
          ? new File([options.file], 'cropped-image.jpg', { type: 'image/jpeg' })
          : options.file;

      formData.append('file', fileToUpload);
    }

    if (options.qualityOnly === true) {
      formData.append('qualityOnly', 'true');
    }

    const result = await patchFormData<UpdatePostImageResp>(url, formData);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'updatePostImage', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/**
 * [DELETE] 刪除文章圖片
 * @param postId - 文章 ID
 * @param imageId - 圖片 ID
 * @returns Promise<CommonResp> - 刪除結果
 */
const deletePostImage = async (postId: string, imageId: string): Promise<CommonResp> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.postImages.deletePostImage}`;
    const result = await del<CommonResp>(url, { postId, imageId });
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'deletePostImage', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/**
 * [POST] 批次上傳文章圖片
 * @param postId - 文章 ID
 * @param files - 圖片檔案陣列（最多 20 張，總大小不超過 100MB）
 * @returns Promise<BatchUploadPostImagesResp> - 批次上傳結果
 */
const batchUploadPostImages = async (
  postId: string,
  files: File[]
): Promise<BatchUploadPostImagesResp> => {
  try {
    const url = `${config.SERVER_POINT}${apiPath.postImages.batchUploadPostImages}`;
    const formData = new FormData();
    formData.append('postId', postId);
    // qualityOnly 固定為 false，不傳送讓後端使用預設值

    // 將所有檔案加入 FormData
    files.forEach(file => {
      formData.append('files', file);
    });

    const result = await postFormData<BatchUploadPostImagesResp>(url, formData);
    return result;
  } catch (error) {
    _EHS.errorReport(error, 'batchUploadPostImages', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

/**
 * [GET] 取得圖片 Blob（用於編輯，解決 CORS 問題）
 * @param postId - 文章 ID
 * @param imageId - 圖片 ID
 * @returns Promise<Blob> - 圖片 Blob
 * @description 透過後端代理取得 GCS 圖片，避免前端直接請求 GCS 時的 CORS 問題
 */
const getImageBlob = async (postId: string, imageId: string): Promise<Blob> => {
  try {
    // 加上時間戳避免瀏覽器快取，確保每次都能取得最新的圖片
    const timestamp = Date.now();
    const url = `${config.SERVER_POINT}${apiPath.postImages.getImageBlob}?postId=${postId}&imageId=${imageId}&t=${timestamp}`;

    // 使用原生 fetch，因為需要取得 Blob
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include', // 帶上 cookies（JWT token）
      headers: {
        // 不需要 Content-Type，因為是 GET 請求
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    return blob;
  } catch (error) {
    _EHS.errorReport(error, 'getImageBlob', _EHS._LEVEL.ERROR);
    return Promise.reject(error);
  }
};

export default {
  getPostImages,
  uploadPostImage,
  batchUploadPostImages,
  updatePostImage,
  deletePostImage,
  getImageBlob
};
